import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_BTN_SECONDARY,
  AE_EDITABLE_INPUT,
  AE_ICON_SIZE,
  AE_LABEL,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';
import { BuilderIconButton } from './BuilderIconButton';
import {
  newMaturityBandId,
  syncBandEdges,
  type AeMaturityBand,
  type AeMaturityMapConfig,
} from './maturityMapUtils';

interface MaturityMapEditorProps {
  value: AeMaturityMapConfig;
  readOnly?: boolean;
  onChange: (next: AeMaturityMapConfig) => void;
}

/**
 * Setup: optional maturity % bands (off by default).
 * Half-open intervals; last band ends at 100%.
 */
export const MaturityMapEditor: React.FC<MaturityMapEditorProps> = ({
  value,
  readOnly = false,
  onChange,
}) => {
  const { t } = useLanguage();
  const bands = value.bands;

  const setEnabled = (enabled: boolean) => {
    onChange({ ...value, enabled });
  };

  const setBands = (next: AeMaturityBand[]) => {
    onChange({ ...value, bands: syncBandEdges(next) });
  };

  const updateBand = (id: string, patch: Partial<AeMaturityBand>) => {
    setBands(bands.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const addBand = () => {
    const sorted = syncBandEdges(bands);
    const last = sorted[sorted.length - 1];
    if (!last) {
      setBands([
        {
          id: newMaturityBandId(),
          label: '',
          min_pct: 0,
          max_pct: 100,
          sort_order: 0,
        },
      ]);
      return;
    }
    // Split last band at midpoint so both stay valid until user edits.
    const mid = Math.round((last.min_pct + last.max_pct) / 2);
    const splitAt = mid > last.min_pct && mid < last.max_pct ? mid : last.min_pct + 1;
    const updatedLast = { ...last, max_pct: splitAt };
    const neu: AeMaturityBand = {
      id: newMaturityBandId(),
      label: '',
      min_pct: splitAt,
      max_pct: 100,
      sort_order: sorted.length,
    };
    setBands([...sorted.slice(0, -1), updatedLast, neu]);
  };

  const removeBand = (id: string) => {
    if (bands.length <= 1) return;
    setBands(bands.filter((b) => b.id !== id));
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
        <input
          type="checkbox"
          className="rounded border-[var(--color-border)]"
          checked={value.enabled}
          disabled={readOnly}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        {t('assessmentEngine.builder.maturityMapEnable')}
      </label>
      <p className={AE_MUTED}>{t('assessmentEngine.builder.maturityMapHint')}</p>

      {value.enabled && (
        <div className="space-y-2">
          <div className="grid grid-cols-[minmax(0,1.5fr)_4.5rem_4.5rem_2rem] gap-2 items-end">
            <span className={AE_LABEL}>{t('assessmentEngine.builder.maturityBandLabel')}</span>
            <span className={AE_LABEL}>{t('assessmentEngine.builder.maturityMinPct')}</span>
            <span className={AE_LABEL}>{t('assessmentEngine.builder.maturityMaxPct')}</span>
            <span className="sr-only">{t('assessmentEngine.builder.remove')}</span>
          </div>
          {bands.map((band, idx) => {
            const isLast = idx === bands.length - 1;
            const isFirst = idx === 0;
            return (
              <div
                key={band.id}
                className="grid grid-cols-[minmax(0,1.5fr)_4.5rem_4.5rem_2rem] gap-2 items-center"
              >
                <input
                  className={AE_EDITABLE_INPUT}
                  value={band.label}
                  disabled={readOnly}
                  placeholder={t('assessmentEngine.builder.maturityBandLabelPlaceholder')}
                  onChange={(e) => updateBand(band.id, { label: e.target.value })}
                />
                <input
                  type="number"
                  className={AE_EDITABLE_INPUT}
                  value={band.min_pct}
                  disabled={readOnly || isFirst}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(e) =>
                    updateBand(band.id, { min_pct: Number(e.target.value) })
                  }
                />
                <input
                  type="number"
                  className={AE_EDITABLE_INPUT}
                  value={band.max_pct}
                  disabled={readOnly || isLast}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(e) =>
                    updateBand(band.id, { max_pct: Number(e.target.value) })
                  }
                />
                {!readOnly ? (
                  <BuilderIconButton
                    label={t('assessmentEngine.builder.remove')}
                    variant="danger"
                    onClick={() => removeBand(band.id)}
                    disabled={bands.length <= 1}
                  >
                    <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                  </BuilderIconButton>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
          {!readOnly && (
            <button type="button" className={AE_BTN_SECONDARY} onClick={addBand}>
              <PlusIcon className={`${AE_ICON_SIZE} inline mr-1`} aria-hidden />
              {t('assessmentEngine.builder.maturityAddBand')}
            </button>
          )}
          <p className={`${AE_MUTED} text-xs`}>
            {t('assessmentEngine.builder.maturityHalfOpenHint')}
          </p>
        </div>
      )}
    </div>
  );
};
